import React, { useCallback, useState, useEffect, useRef } from 'react';
import EditableText from './EditableText';
import { Alert, Button, Space, Spin, Tooltip } from 'antd';
import { RollbackOutlined } from '@ant-design/icons';

export interface CurrencyPairRate {
  rate: number;
  sourceCurrency: string;
  targetCurrency: string;
}

export interface CurrencyRateMessageProps {
  id?: string;
  sourceCurrency: string;
  targetCurrency: string;
  currencyRate?: number;
  sourceCurrencyAmount?: number;
  onChange?: (value: CurrencyPairRate | undefined) => void;
  resettable?: {
    canReset: boolean;
    onReset: () => Promise<number>;
  };
}

const CurrencyRateMessage: React.FC<CurrencyRateMessageProps> = ({
  id,
  sourceCurrency,
  targetCurrency,
  currencyRate,
  sourceCurrencyAmount,
  onChange,
  resettable,
}) => {
  if (!currencyRate)
    return (
      <Alert
        message={
          <div style={{ textAlign: 'center', width: '100%' }}>
            <Spin spinning={true} />
          </div>
        }
        type="info"
      />
    );

  const [configuredRate, setConfiguredRate] = useState(currencyRate);
  const [receivedAmount, setReceivedAmount] = useState(
    (sourceCurrencyAmount ?? 0) * currencyRate,
  );
  const [isUserEdited, setIsUserEdited] = useState(false);

  const hasInitialChangeBeenCalled = useRef(false);

  const updateNewRate = useCallback(
    (newRate: number) => {
      setConfiguredRate(newRate);
      onChange?.({ rate: newRate, sourceCurrency, targetCurrency });
    },
    [onChange, sourceCurrency, targetCurrency],
  );

  useEffect(() => {
    if (!isUserEdited) {
      setConfiguredRate(currencyRate);
      setReceivedAmount((sourceCurrencyAmount ?? 0) * currencyRate);
    } else {
      setReceivedAmount((sourceCurrencyAmount ?? 0) * configuredRate);
    }
  }, [sourceCurrencyAmount, configuredRate, currencyRate, isUserEdited]);

  useEffect(() => {
    setIsUserEdited(false);
    hasInitialChangeBeenCalled.current = false;
  }, [sourceCurrency, targetCurrency]);

  useEffect(() => {
    if (currencyRate && !hasInitialChangeBeenCalled.current) {
      onChange?.({
        rate: configuredRate,
        sourceCurrency,
        targetCurrency,
      });
      hasInitialChangeBeenCalled.current = true;
    }
  }, [currencyRate, configuredRate, sourceCurrency, targetCurrency, onChange]);

  const handleSaveRate = useCallback(
    (value: string) => {
      const leastSigCurrencyAmount = parseFloat(value);
      if (isNaN(leastSigCurrencyAmount) || leastSigCurrencyAmount <= 0)
        return false;

      const newRate =
        currencyRate < 1
          ? 1.0 / leastSigCurrencyAmount
          : leastSigCurrencyAmount;
      setIsUserEdited(true);
      setReceivedAmount((sourceCurrencyAmount ?? 0) * newRate);
      updateNewRate(newRate);
      return true;
    },
    [sourceCurrencyAmount, currencyRate, updateNewRate],
  );

  const handleSaveReceivedAmount = useCallback(
    (value: string) => {
      const targetCurrencyReceivedAmount = parseFloat(value);
      if (
        isNaN(targetCurrencyReceivedAmount) ||
        targetCurrencyReceivedAmount <= 0
      )
        return false;

      const newRate =
        targetCurrencyReceivedAmount / (sourceCurrencyAmount ?? 1);
      setIsUserEdited(true);
      setReceivedAmount(targetCurrencyReceivedAmount);
      updateNewRate(newRate);
      return true;
    },
    [sourceCurrencyAmount, updateNewRate],
  );

  const effectiveRate = isUserEdited ? configuredRate : currencyRate;
  const lessSignificantCurrencyAmount =
    effectiveRate < 1 ? 1 / effectiveRate : effectiveRate;

  const lessSignificantCurrency =
    effectiveRate < 1 ? sourceCurrency : targetCurrency;
  const moreSignificantCurrency =
    effectiveRate < 1 ? targetCurrency : sourceCurrency;

  const rateElem = (
    <EditableText
      id={id && `rate-msg-rate-input-${id}`}
      inputType="number"
      value={lessSignificantCurrencyAmount.toFixed(2)}
      onSave={handleSaveRate}
    />
  );

  const resetElement = (resettable?.canReset ?? false) && (
    <Tooltip title="Reset Exchange Rate">
      <Button
        id={id && `rate-msg-rate-reset-${id}`}
        style={{
          marginLeft: '10px',
          padding: '0px 5px',
          height: 'unset',
        }}
        onClick={async () => {
          const newRate = await resettable?.onReset();
          handleSaveRate(newRate?.toString() ?? '');
        }}
      >
        <RollbackOutlined />
      </Button>
    </Tooltip>
  );

  const message = sourceCurrencyAmount ? (
    <span>
      Current rate: 1 {moreSignificantCurrency} = {rateElem}{' '}
      {lessSignificantCurrency}. Receives:{' '}
      <EditableText
        id={id && `rate-msg-rate-amount-${id}`}
        inputType="number"
        value={receivedAmount.toFixed(2)}
        onSave={handleSaveReceivedAmount}
      />{' '}
      {targetCurrency}.{resetElement}
    </span>
  ) : (
    <span>
      Current rate: 1 {moreSignificantCurrency} = {rateElem}{' '}
      {lessSignificantCurrency}.{resetElement}
    </span>
  );

  return <Alert message={message} type="info" showIcon />;
};

export { CurrencyRateMessage };
